import { useCallback, useEffect, useRef } from 'react';
import { callDeepLink } from '@croquiscom-pvt/web2app';
import { Badge, ContentHeader } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import getConfig from 'next/config';

import { isZigzag } from '@common/device-manager';
import { useOnceReveal } from '@common/hooks';
import { useIsAuthenticated } from '@domains/pdp-common';

import { ActualSizeItemSection } from '../components/actual-size/ActualSizeItemSection';
import { EmptyView } from '../components/actual-size/EmptyView';
import { consumeShouldOpenDirectInput } from '../constant/local-storage';
import { useSizeInquiry } from '../hooks/useSizeInquiry';
import { useActualSizeUBL } from '../log/useActualSizeUBL';
import { useActualSizeItemList } from '../services/actual-size';

export const ActualSizeContainer: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { rawItems, isLoading, catalogProductId } = useActualSizeItemList();

  const { isLoggedIn, isLoading: isAuthLoading } = useIsAuthenticated();
  const directInputOpenedRef = useRef(false);

  // Broadcast 리스너는 각 ActualSizeItemSection에서 개별적으로 등록

  // 로그인 복귀 시 직접 입력 페이지 열기 (1회 한정)
  useEffect(() => {
    if (directInputOpenedRef.current) return;
    if (isAuthLoading || !isLoggedIn) return;

    const redirectData = consumeShouldOpenDirectInput();
    if (!redirectData) return;

    directInputOpenedRef.current = true;

    const { config } = getConfig().publicRuntimeConfig;
    const directInputUrl = `${config.appUrl}/products/${redirectData.catalogProductId}/direct-size-input?size_category_id=${redirectData.sizeCategoryId}`;

    if (isZigzag()) {
      callDeepLink('/full_screen_simple_browser')({ url: encodeURIComponent(directInputUrl), input_accessory: true });
    } else {
      location.href = directInputUrl;
    }
  }, [isLoggedIn, isAuthLoading]);

  // UBL
  const { sectionImpressionUBL, emptyViewImpressionUBL, emptyViewClickUBL } = useActualSizeUBL();

  const { targetRef, isRevealed } = useOnceReveal();

  // 사이즈 비교 가능 여부 (하나라도 비교 가능하면 Beta 뱃지 노출)
  const hasAnyComparableItem = rawItems.some((item) => item.isSizeComparable);

  const { handleSizeInquiry } = useSizeInquiry(catalogProductId);

  const handleEmptyViewClick = useCallback(() => {
    emptyViewClickUBL();
    handleSizeInquiry();
  }, [emptyViewClickUBL, handleSizeInquiry]);

  useEffect(() => {
    if (isRevealed) {
      sectionImpressionUBL();

      const isEmpty = rawItems.length === 0;
      if (isEmpty) {
        emptyViewImpressionUBL();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealed]);

  if (isLoading) return null;

  const isEmpty = rawItems.length === 0;

  return (
    <Container>
      <Wrapper ref={targetRef}>
        <ContentHeader
          title={
            hasAnyComparableItem ? (
              <TitleWithBadge>
                실측 사이즈 비교하기
                <Badge
                  size='medium'
                  borderColor='transparent'
                  backgroundColor='subSkyBlueBackground'
                  textColor='subSkyBluePrimary'
                >
                  Beta
                </Badge>
              </TitleWithBadge>
            ) : (
              '실측 사이즈'
            )
          }
        />

        {isEmpty ? (
          <EmptyView onClick={handleEmptyViewClick} />
        ) : (
          <MeasurementContainer>
            {rawItems.map((rawItem, index) => (
              <ActualSizeItemSection
                key={rawItem.sizeCategoryId || index}
                rawItem={rawItem}
                isFirst={index === 0}
                isRevealed={isRevealed}
              />
            ))}
          </MeasurementContainer>
        )}
      </Wrapper>
    </Container>
  );
};

const Wrapper = styled.div`
  position: relative;
  padding-bottom: 24px;
`;

const Container = styled.div`
  background-color: ${vars.color.backgroundSurface};
`;

const MeasurementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TitleWithBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
